import React from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  KeyboardAvoidingView,
} from "react-native";

import Utils, {
  escapeRegexp,
  hasItems,
  isAndroid,
  navigate,
} from "../../../helpers/utils";
import appStyles from "../../appStyles";
import Header from "../common/header";
import Search from "../common/search-bar/Search";
import MenuEditOptionsPanel from "../common/MenuEditOptionsPanel";
import ManaConfirmDialog from "../common/dialog/ManaConfirmDialog";
import RoutinesPageFabButton from "./RoutinesPageFabButton";
import RoutineItem from "./RoutineItem";
import AppMessages from "../../../config/AppMessages";
import NoResultsFound from "./NoResultsFound";

export default class RoutinesPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      routines: [],
      showDeleteConfirmation: false,
      selectedRoutine: null,
    };
  }

  componentDidMount = () => {
    if (this.controller.hasNavigation()) {
      this.focusListenerUnsub = this.props.navigation.addListener(
        "focus",
        () => {
          this.componentDidFocus();
        },
      );
    } else {
      this.componentDidFocus();
    }
  };

  componentWillUnmount = async () => {
    if (this.focusListenerUnsub) this.focusListenerUnsub();
  };

  componentDidFocus = async () => {
    const routines = this.controller.state.routines;
    this.setState((state) => ({ ...state, routines, allRoutines: routines }));
  };

  filterResults = ({ term }) => {
    const { allRoutines } = this.state;

    let filtered = [...allRoutines];

    // Multi terms search
    if (term) {
      const pattern = Utils.getSearchPattern(term);
      filtered = filtered.filter((a) => {
        return a.name && a.name.match(pattern);
      });
    }

    this.setState((state) => ({
      ...state,
      routines: [...filtered],
    }));
  };

  // ------------------------------------- FAB -------------------------------------
  createNewRoutine = (routineType = "day") => {
    navigate(this.controller, "rn:edit_routine", "changepage", {
      routine: { time_interval: routineType },
    });
  };

  // ------------------------------------- Menu Options -------------------------------------
  toggleEditMenuOptions = (routine) => {
    this.setState((state) => ({
      ...state,
      selectedRoutine: routine,
    }));
  };

  editRoutine = (routine) => {
    this.toggleEditMenuOptions();
    //TODO: make routine JSON serializable, currently causes "Converting circular structure to JSON"
    navigate(this.controller, "rn:edit_routine", "changepage", { routine });
  };

  duplicateRoutine = (routine) => {
    this.toggleEditMenuOptions();
    delete routine.id;
    routine.isDuplicate = true;

    const matches = routine.name?.match(/\(\d+\)/);
    if (!hasItems(matches)) {
      routine.name = routine.name + " (1)";
      digit = 0;
    } else {
      digit = matches[0].replace(/[^0-9.]/g, "");
      routine.name = routine.name?.replace(/\(\d+\)/g, "(" + ++digit + ")");
    }

    navigate(this.controller, "rn:edit_routine", "changepage", { routine });
  };

  toggleDeleteDialog = (isOpen) => {
    this.setState((state) => ({
      ...state,
      showDeleteConfirmation: isOpen,
    }));
  };

  confirmedDelete = () => {
    const { selectedRoutine } = this.state;
    if (!selectedRoutine) return;

    this.toggleDeleteDialog(false);
    this.toggleEditMenuOptions();

    this.controller.tracking.track("Delete routine");
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "routine",
        id: selectedRoutine.id,
        is_active: false,
      },
      () => {
        this.controller.cardAction({ action: "refresh" });
        this.componentDidFocus();
      },
    );
  };

  goBack = () => {
    navigate(this.controller, "rn:home_card");
  };

  render() {
    const { routines, selectedRoutine, showDeleteConfirmation } = this.state;

    return (
      <View style={[appStyles.container, styles.container]}>
        <KeyboardAvoidingView
          style={appStyles.kbViewNoFooter}
          behavior="height"
        >
          <Header
            title="Routines"
            subTitle="Edit your routines"
            hideAvatar
            controller={this.controller}
            goBack={this.goBack}
          />

          {/* <TouchableOpacity
                style={[styles.row, appStyles.borderBottom]}
                onPress={() => this.editRoutine(routine)}
                onLongPress={() => this.toggleEditMenuOptions(routine)}
              >
                />
              </TouchableOpacity> */}

          <ScrollView
            style={appStyles.scrollView}
            contentContainerStyle={appStyles.scrollViewContent}
          >
            {hasItems(routines) ? (
              routines.map((routine, index) => (
                <RoutineItem
                  routine={routine}
                  key={index}
                  onPress={this.editRoutine}
                  onLongPress={this.toggleEditMenuOptions}
                />
              ))
            ) : (
              <NoResultsFound
                message={AppMessages.RoutinesNoResultsMessage}
                buttons={[
                  {
                    action: () => this.createNewRoutine("day"),
                    text: AppMessages.CreateDayRoutineButtonText,
                  },
                  {
                    action: () => this.createNewRoutine("week"),
                    text: AppMessages.CreateWeekRoutineButtonText,
                  },
                ]}
              />
            )}
          </ScrollView>

          <RoutinesPageFabButton createNewRoutine={this.createNewRoutine} />

          <Search
            showIcon={false}
            controller={this.controller}
            placeholder="Search Routines"
            filterResults={(term) => this.filterResults(term)}
            style={isAndroid ? { bottom: 15 } : { bottom: 5 }}
          />

          {selectedRoutine ? (
            <MenuEditOptionsPanel
              isPanelActive={!!selectedRoutine}
              close={() => this.toggleEditMenuOptions(null)}
              options={[
                {
                  text: "Edit Routine",
                  action: () => this.editRoutine(selectedRoutine),
                },
                {
                  text: "Duplicate Routine",
                  action: () => this.duplicateRoutine(selectedRoutine),
                },
                {
                  text: "Remove Routine",
                  action: () => this.toggleDeleteDialog(true),
                  isDelete: true,
                },
              ]}
            />
          ) : null}

          <ManaConfirmDialog
            isOpen={showDeleteConfirmation}
            title="Are you sure you want to remove this routine?"
            description=""
            onConfirm={this.confirmedDelete}
            onClose={() => this.toggleDeleteDialog(false)}
          />
        </KeyboardAvoidingView>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FFF",
  },
});
